import {type FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {styled} from 'styled-components';
import {useLocation, useSearch} from 'wouter';

import type {NzbMediaMetadata} from '@repo/shared/api/nzb_media_metadata';
import type {
  NzbCheckMovie,
  NzbCheckResult,
  NzbHealthDebugData,
  RecentNzbCheck,
  RecentNzbChecksData,
} from '@repo/shared/api/website_api';
import {NO_IMDB_ATTRIBUTE} from '@repo/shared/constant';
import {formatNzbsuCategory} from '@repo/shared/lib/nzbsu_category';
import {errorAsString} from '@repo/shared/lib/type_utils';

import {Button, UnthemedNavLink} from '@repo/shared-web/components/core/button';
import {Input} from '@repo/shared-web/components/core/input';
import {useStringQueryString} from '@repo/shared-web/lib/use_query_string';

import {apiCall} from '@repo/website-frontend/lib/api';
import {formatBytes} from '@repo/website-frontend/lib/bytes';
import {formatReleaseDate} from '@repo/website-frontend/lib/date';

const PAGE_SIZE = 50;
const INCREMENTAL_PAGE_SIZE = 100;
const CHECK_RATE_SAMPLE_SIZE = 100;
const FAST_POLL_MS = 2_000;
const MEDIUM_POLL_MS = 60_000;
const SLOW_POLL_MS = 10 * 60_000;
const MEDIUM_POLL_AFTER_MS = 5 * 60_000;
const SLOW_POLL_AFTER_MS = 60 * 60_000;

function mergeRecentChecks(
  primary: RecentNzbCheck[],
  secondary: RecentNzbCheck[],
): RecentNzbCheck[] {
  const byId = new Map<string, RecentNzbCheck>();
  for (const item of primary) {
    byId.set(item.id, item);
  }
  for (const item of secondary) {
    if (!byId.has(item.id)) {
      byId.set(item.id, item);
    }
  }
  return [...byId.values()].sort((a, b) => b.ts - a.ts);
}

function getNextPollDelay(pageEnteredAt: number): number {
  const elapsed = Date.now() - pageEnteredAt;
  if (elapsed < MEDIUM_POLL_AFTER_MS) {
    return Math.min(FAST_POLL_MS, MEDIUM_POLL_AFTER_MS - elapsed);
  }
  if (elapsed < SLOW_POLL_AFTER_MS) {
    return Math.min(MEDIUM_POLL_MS, SLOW_POLL_AFTER_MS - elapsed);
  }
  return SLOW_POLL_MS;
}

type TimingKey = keyof Pick<
  NzbCheckResult,
  | 'releaseLookupDurationMs'
  | 'tmdbLookupDurationMs'
  | 'downloadDurationMs'
  | 'parseDurationMs'
  | 'sampleDurationMs'
  | 'nntpConnectDurationMs'
  | 'nntpGroupDurationMs'
  | 'nntpHeaderDurationMs'
  | 'nntpQuitDurationMs'
  | 'probeDurationMs'
>;

const TIMING_COLUMNS: {key: TimingKey; label: string}[] = [
  {key: 'releaseLookupDurationMs', label: 'NZBSU lookup'},
  {key: 'tmdbLookupDurationMs', label: 'TMDB lookup'},
  {key: 'downloadDurationMs', label: 'Download'},
  {key: 'parseDurationMs', label: 'Parse'},
  {key: 'sampleDurationMs', label: 'Sample'},
  {key: 'nntpConnectDurationMs', label: 'NNTP connect'},
  {key: 'nntpGroupDurationMs', label: 'NNTP GROUP'},
  {key: 'nntpHeaderDurationMs', label: 'NNTP HEAD'},
  {key: 'nntpQuitDurationMs', label: 'NNTP QUIT'},
  {key: 'probeDurationMs', label: 'Probe total'},
];

export const NzbHealthDebugPage: FC = () => {
  const [requestedGuid, setRequestedGuid, requestedGuidRef] = useStringQueryString('guid', '');
  const [location, setLocation] = useLocation();
  const search = useSearch();
  const [searchGuid, setSearchGuid] = useState(requestedGuid);
  const [refreshKey, setRefreshKey] = useState(0);
  const [recentChecks, setRecentChecks] = useState<RecentNzbChecksData | undefined>();
  const [releaseDetails, setReleaseDetails] = useState<NzbHealthDebugData | undefined>();
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [loadingRelease, setLoadingRelease] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [showDetailedTimes, setShowDetailedTimes] = useState(false);
  const [movieInfoByNzbId, setMovieInfoByNzbId] = useState<Record<string, NzbCheckMovie>>({});
  const checksPerMinute = useMemo(
    () => getChecksPerMinute(recentChecks?.items),
    [recentChecks?.items],
  );
  const pageEnteredAtRef = useRef(Date.now());
  const mostRecentCheckTsRef = useRef<number>();
  const mostRecentCheckIdsRef = useRef<string[]>([]);
  const hasSeenRecentCheckRef = useRef(false);
  const recentChecksInitializedRef = useRef(false);
  const requestedMovieIdsRef = useRef(new Set<string>());
  const mountedRef = useRef(false);

  const updateRecentCheckCursor = useCallback((items: RecentNzbCheck[]) => {
    if (items.length === 0) {
      return;
    }
    const newestTs = items.reduce((latest, item) => Math.max(latest, item.ts), 0);
    const currentTs = mostRecentCheckTsRef.current;
    if (!hasSeenRecentCheckRef.current || currentTs === undefined || newestTs > currentTs) {
      hasSeenRecentCheckRef.current = true;
      mostRecentCheckTsRef.current = newestTs;
      mostRecentCheckIdsRef.current = items
        .filter(item => item.ts === newestTs)
        .map(item => item.id);
    } else if (newestTs === currentTs) {
      mostRecentCheckIdsRef.current = [
        ...new Set([
          ...mostRecentCheckIdsRef.current,
          ...items.filter(item => item.ts === currentTs).map(item => item.id),
        ]),
      ];
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setSearchGuid(requestedGuid);
  }, [requestedGuid]);

  useEffect(() => {
    let active = true;
    setLoadingRecent(true);
    setError(undefined);
    apiCall('POST /list-recent-nzb-checks', {paginationToken: undefined, limit: PAGE_SIZE})
      .then(result => {
        if (active) {
          const newestTs = result.items.reduce((latest, item) => Math.max(latest, item.ts), 0);
          if (newestTs > 0) {
            updateRecentCheckCursor(result.items);
          } else if (mostRecentCheckTsRef.current === undefined) {
            mostRecentCheckTsRef.current = pageEnteredAtRef.current;
          }
          recentChecksInitializedRef.current = true;
          setRecentChecks(current =>
            current
              ? {
                  items: mergeRecentChecks(current.items, result.items),
                  paginationToken: result.paginationToken,
                }
              : result,
          );
        }
      })
      .catch(err => {
        if (active) {
          setError(errorAsString(err));
        }
      })
      .finally(() => {
        if (active) {
          setLoadingRecent(false);
        }
      });
    return () => {
      active = false;
    };
  }, [refreshKey, updateRecentCheckCursor]);

  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = (): void => {
      if (active) {
        timer = setTimeout(() => {
          void poll();
        }, getNextPollDelay(pageEnteredAtRef.current));
      }
    };

    const poll = async (): Promise<void> => {
      if (!active) {
        return;
      }
      if (!recentChecksInitializedRef.current) {
        schedule();
        return;
      }

      try {
        const sinceTs = Math.max(0, mostRecentCheckTsRef.current ?? 0);
        const newItems: RecentNzbCheck[] = [];
        let paginationToken: string | undefined;
        do {
          const page = await apiCall('POST /list-newer-nzb-checks', {
            sinceTs,
            excludeIds: mostRecentCheckIdsRef.current,
            limit: INCREMENTAL_PAGE_SIZE,
            paginationToken,
          });
          if (!active) {
            return;
          }
          newItems.push(...page.items);
          paginationToken = page.paginationToken;
        } while (paginationToken);

        if (newItems.length > 0) {
          updateRecentCheckCursor(newItems);
          setRecentChecks(current => {
            if (!current) {
              return {items: newItems, paginationToken: undefined};
            }
            const items = mergeRecentChecks(newItems, current.items);
            return items.length === current.items.length ? current : {...current, items};
          });
        }
      } catch {
        // A transient polling failure leaves the current table visible; the next poll retries.
      }

      schedule();
    };

    timer = setTimeout(() => void poll(), getNextPollDelay(pageEnteredAtRef.current));

    return () => {
      active = false;
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    };
  }, [updateRecentCheckCursor]);

  useEffect(() => {
    const guid = requestedGuid.trim();
    if (!guid) {
      setReleaseDetails(undefined);
      setLoadingRelease(false);
      return;
    }

    let active = true;
    setLoadingRelease(true);
    setReleaseDetails(undefined);
    setError(undefined);
    apiCall('POST /get-nzb-health-debug', {guid, paginationToken: undefined, limit: PAGE_SIZE})
      .then(result => {
        if (active) {
          setReleaseDetails(result);
        }
      })
      .catch(err => {
        if (active) {
          setError(errorAsString(err));
        }
      })
      .finally(() => {
        if (active) {
          setLoadingRelease(false);
        }
      });
    return () => {
      active = false;
    };
  }, [refreshKey, requestedGuid]);

  const displayedNzbIds = useMemo(() => {
    const checks = requestedGuid
      ? releaseDetails?.guid === requestedGuid
        ? releaseDetails.checks
        : undefined
      : recentChecks?.items;
    return [...new Set(checks?.map(check => check.nzbId) ?? [])];
  }, [recentChecks?.items, releaseDetails?.checks, requestedGuid]);

  useEffect(() => {
    if (displayedNzbIds.length === 0) {
      return;
    }
    const nzbIds = displayedNzbIds.filter(nzbId => !requestedMovieIdsRef.current.has(nzbId));
    if (nzbIds.length === 0) {
      return;
    }
    nzbIds.forEach(nzbId => requestedMovieIdsRef.current.add(nzbId));
    apiCall('POST /get-nzb-check-movies', {nzbIds})
      .then(({items}) => {
        if (mountedRef.current) {
          setMovieInfoByNzbId(current => ({
            ...current,
            ...Object.fromEntries(items.map(item => [item.nzbId, item])),
          }));
        }
      })
      .catch(err => {
        nzbIds.forEach(nzbId => requestedMovieIdsRef.current.delete(nzbId));
        if (mountedRef.current) {
          setError(errorAsString(err));
        }
      });
  }, [displayedNzbIds, refreshKey]);

  const handleRefreshClick = useCallback(() => {
    setRefreshKey(current => current + 1);
  }, []);

  const handleNavigateToRelease = useCallback(
    (guid: string) => {
      const query = new URLSearchParams(search);
      query.set('guid', guid);
      setLocation(`${location}?${query.toString()}`);
    },
    [location, search, setLocation],
  );

  const handleInspectClick = useCallback(() => {
    handleNavigateToRelease(searchGuid.trim());
  }, [handleNavigateToRelease, searchGuid]);

  const handleCloseReleaseClick = useCallback(() => {
    setRequestedGuid('');
    setReleaseDetails(undefined);
  }, [setRequestedGuid]);

  const handleLoadMoreClick = useCallback(async () => {
    if (!recentChecks?.paginationToken) {
      return;
    }
    setLoadingMore(true);
    setError(undefined);
    try {
      const nextPage = await apiCall('POST /list-recent-nzb-checks', {
        limit: PAGE_SIZE,
        paginationToken: recentChecks.paginationToken,
      });
      setRecentChecks(current =>
        current
          ? {
              items: mergeRecentChecks(current.items, nextPage.items),
              paginationToken: nextPage.paginationToken,
            }
          : current,
      );
    } catch (err: unknown) {
      setError(errorAsString(err));
    } finally {
      setLoadingMore(false);
    }
  }, [recentChecks?.paginationToken]);

  const handleLoadOlderReleaseChecksClick = useCallback(async () => {
    if (!requestedGuid || !releaseDetails?.paginationToken) {
      return;
    }
    setLoadingMore(true);
    setError(undefined);
    try {
      const nextPage = await apiCall('POST /get-nzb-health-debug', {
        guid: requestedGuid,
        limit: PAGE_SIZE,
        paginationToken: releaseDetails.paginationToken,
      });
      setReleaseDetails(current =>
        requestedGuidRef.current === requestedGuid && current?.guid === requestedGuid
          ? {
              ...current,
              checks: [...current.checks, ...nextPage.checks],
              paginationToken: nextPage.paginationToken,
            }
          : current,
      );
    } catch (err: unknown) {
      if (requestedGuidRef.current === requestedGuid) {
        setError(errorAsString(err));
      }
    } finally {
      setLoadingMore(false);
    }
  }, [releaseDetails?.paginationToken, requestedGuid, requestedGuidRef]);

  const handleRunCheckClick = useCallback(async () => {
    await apiCall('POST /check-nzb', {id: requestedGuid});
    setRefreshKey(current => current + 1);
  }, [requestedGuid]);

  const handleDownloadNzbClick = useCallback(async () => {
    if (!releaseDetails) {
      return;
    }
    const guid = releaseDetails.guid;
    const {url} = await apiCall('POST /get-nzb-link', {
      id: guid,
      fileName: `${releaseDetails.release?.title ?? guid}.nzb`,
    });
    const element = document.createElement('a');
    element.href = url;
    element.download = `${guid}.nzb`;
    element.style.display = 'none';
    document.body.append(element);
    element.click();
    element.remove();
  }, [releaseDetails]);

  return (
    <Wrapper>
      <PageHeader>
        <h1>NZB checks</h1>
        <ActionGroup>
          <CheckRate>{formatCheckRate(checksPerMinute)} NZBs/min</CheckRate>
          <ShowDetailedTimes>
            <input
              type="checkbox"
              checked={showDetailedTimes}
              onChange={evt => setShowDetailedTimes(evt.target.checked)}
            />
            Show detailed times
          </ShowDetailedTimes>
          {requestedGuid ? (
            <Button onClick={handleCloseReleaseClick}>Back to checks</Button>
          ) : (
            <Button
              onClick={handleRefreshClick}
              disabled={loadingRecent}
              overrides={{
                paddingTop: 4,
                paddingRight: 10,
                paddingBottom: 4,
                paddingLeft: 10,
                fontSize: 13,
                lineHeight: 1.2,
              }}
            >
              Refresh
            </Button>
          )}
        </ActionGroup>
      </PageHeader>

      {error ? <ErrorMessage>{error}</ErrorMessage> : <></>}

      {requestedGuid ? (
        <Panel>
          {loadingRelease ? (
            <Message>Loading release details…</Message>
          ) : releaseDetails ? (
            <>
              <SectionHeader>
                <div>
                  <h2>{releaseDetails.release?.title ?? 'Release details'}</h2>
                  <Guid
                    to={`/debug/nzb-health?guid=${encodeURIComponent(releaseDetails.guid)}`}
                    overrides={{enableSelect: true}}
                  >
                    {releaseDetails.guid}
                  </Guid>
                  {releaseDetails.release ? (
                    <ReleaseDate>
                      Posted {formatReleaseDate(releaseDetails.release.pubTs)}
                    </ReleaseDate>
                  ) : (
                    <></>
                  )}
                </div>
                <ActionGroup>
                  <Button onClickAsync={handleDownloadNzbClick}>Download NZB</Button>
                  <DetailsLink
                    to={`/nzbsu?search=${encodeURIComponent(
                      releaseDetails.release?.title ?? releaseDetails.guid,
                    )}`}
                  >
                    Find on NZBSU
                  </DetailsLink>
                  <Button onClickAsync={handleRunCheckClick}>Check now</Button>
                </ActionGroup>
              </SectionHeader>

              <FileDetailsHeading>NZB file details</FileDetailsHeading>
              <FileMetadata aria-label="NZB file metadata">
                <MetadataField>
                  <dt>Release size</dt>
                  <dd>
                    {releaseDetails.release ? formatBytes(releaseDetails.release.size, 1) : '—'}
                  </dd>
                </MetadataField>
                <MetadataField>
                  <dt>NZBSU grabs</dt>
                  <dd>{releaseDetails.release?.grabs.toLocaleString() ?? '—'}</dd>
                </MetadataField>
                <MetadataField>
                  <dt>IMDb</dt>
                  <dd>
                    {!releaseDetails.release
                      ? '—'
                      : releaseDetails.release.imdb === NO_IMDB_ATTRIBUTE
                        ? 'Not linked'
                        : releaseDetails.release.imdb}
                  </dd>
                </MetadataField>
                <MetadataField>
                  <dt>NFO</dt>
                  <dd>
                    {releaseDetails.release
                      ? releaseDetails.release.nfo
                        ? 'Available'
                        : 'Not available'
                      : '—'}
                  </dd>
                </MetadataField>
                <MetadataField>
                  <dt>Stored NZB size</dt>
                  <dd>
                    {releaseDetails.manifest.contentLength === undefined
                      ? releaseDetails.manifest.exists
                        ? 'Unknown'
                        : 'Not cached'
                      : formatBytes(releaseDetails.manifest.contentLength, 1)}
                  </dd>
                </MetadataField>
                <MetadataField>
                  <dt>Content type</dt>
                  <dd>{releaseDetails.manifest.contentType ?? '—'}</dd>
                </MetadataField>
                <MetadataField>
                  <dt>Content encoding</dt>
                  <dd>{releaseDetails.manifest.contentEncoding ?? '—'}</dd>
                </MetadataField>
                <MetadataField>
                  <dt>Validation marker</dt>
                  <dd>
                    {releaseDetails.manifest.exists
                      ? (releaseDetails.manifest.validationMarker ?? 'None')
                      : 'Not cached'}
                  </dd>
                </MetadataField>
                <MetadataField>
                  <dt>Cache updated</dt>
                  <dd>
                    {releaseDetails.manifest.lastModified === undefined
                      ? '—'
                      : formatTimestamp(releaseDetails.manifest.lastModified)}
                  </dd>
                </MetadataField>
              </FileMetadata>

              <PreviousChecksHeading>Check history</PreviousChecksHeading>
              {releaseDetails.checks.length ? (
                <TableScroll>
                  <ChecksTable>
                    <thead>
                      <tr>
                        <th>Checked</th>
                        <th>Movie</th>
                        <th>NZBSU category</th>
                        <th>Result</th>
                        <th>Track metadata</th>
                        <th>Found</th>
                        <th>Missing</th>
                        <th>Sampled</th>
                        <th>Time</th>
                        {showDetailedTimes
                          ? TIMING_COLUMNS.map(({key, label}) => <th key={key}>{label}</th>)
                          : null}
                      </tr>
                    </thead>
                    <tbody>
                      {releaseDetails.checks.map(check => (
                        <tr key={check.id}>
                          <td>{formatTimestamp(check.ts)}</td>
                          <td>
                            <CheckedMovie movie={movieInfoByNzbId[check.nzbId]} />
                          </td>
                          <td>
                            <CategoryValue title={formatNzbsuCategory(check.nzbsuCategory)}>
                              {formatNzbsuCategory(check.nzbsuCategory) ?? '—'}
                            </CategoryValue>
                          </td>
                          <td>
                            {check.outcome === 'failed'
                              ? 'Check failed'
                              : check.checked === 0
                                ? 'Nothing tested'
                                : check.failure === 0
                                  ? 'Sample passed'
                                  : 'Missing parts'}
                          </td>
                          <td>
                            <TrackMetadataStatus metadata={check.mediaMetadata} />
                          </td>
                          <td>{check.success}</td>
                          <td>{check.failure}</td>
                          <td>{`${check.checked} of ${check.total}`}</td>
                          <td>
                            <TotalDuration>{formatDuration(check.totalDurationMs)}</TotalDuration>
                          </td>
                          {showDetailedTimes
                            ? TIMING_COLUMNS.map(({key}) => (
                                <td key={key}>{formatDuration(check[key])}</td>
                              ))
                            : null}
                        </tr>
                      ))}
                    </tbody>
                  </ChecksTable>
                </TableScroll>
              ) : (
                <Message>No saved checks for this release.</Message>
              )}

              {releaseDetails.paginationToken ? (
                <LoadMore onClickAsync={handleLoadOlderReleaseChecksClick} loading={loadingMore}>
                  Load older checks
                </LoadMore>
              ) : (
                <></>
              )}
            </>
          ) : (
            <Message>
              Release details aren’t available. Go back to the checks list and try again.
            </Message>
          )}
        </Panel>
      ) : (
        <>
          <Panel>
            {loadingRecent ? (
              <Message>Loading recent checks…</Message>
            ) : recentChecks?.items.length ? (
              <TableScroll>
                <ChecksTable>
                  <thead>
                    <tr>
                      <th>Details</th>
                      <th>Checked</th>
                      <th>Release</th>
                      <SizeHeader>Size</SizeHeader>
                      <th>Movie</th>
                      <th>NZBSU category</th>
                      <th>Result</th>
                      <th>Track metadata</th>
                      <th>Found</th>
                      <th>Missing</th>
                      <th>Sampled</th>
                      <th>Time</th>
                      {showDetailedTimes
                        ? TIMING_COLUMNS.map(({key, label}) => <th key={key}>{label}</th>)
                        : null}
                    </tr>
                  </thead>
                  <tbody>
                    {recentChecks.items.map(check => (
                      <tr key={check.id}>
                        <td>
                          <DetailsLink
                            to={`/debug/nzb-health?guid=${encodeURIComponent(check.nzbId)}`}
                          >
                            Details
                          </DetailsLink>
                        </td>
                        <td>{formatTimestamp(check.ts)}</td>
                        <td>
                          <ReleaseTitle>{check.title ?? 'Release title unavailable'}</ReleaseTitle>
                        </td>
                        <SizeCell>
                          {check.size === undefined ? '—' : formatBytes(check.size, 1)}
                        </SizeCell>
                        <td>
                          <CheckedMovie movie={movieInfoByNzbId[check.nzbId]} />
                        </td>
                        <td>
                          <CategoryValue title={formatNzbsuCategory(check.nzbsuCategory)}>
                            {formatNzbsuCategory(check.nzbsuCategory) ?? '—'}
                          </CategoryValue>
                        </td>
                        <td>
                          <ResultBadge
                            $state={
                              check.outcome === 'failed'
                                ? 'failed'
                                : check.checked === 0
                                  ? 'empty'
                                  : check.failure > 0
                                    ? 'missing'
                                    : 'found'
                            }
                          >
                            {check.outcome === 'failed'
                              ? 'Check failed'
                              : check.checked === 0
                                ? 'Nothing tested'
                                : check.failure === 0
                                  ? 'Sample passed'
                                  : 'Missing parts'}
                          </ResultBadge>
                        </td>
                        <td>
                          <TrackMetadataStatus metadata={check.mediaMetadata} />
                        </td>
                        <td>{check.success}</td>
                        <td>{check.failure}</td>
                        <td>{`${check.checked} of ${check.total}`}</td>
                        <td>{formatDuration(check.totalDurationMs)}</td>
                        {showDetailedTimes
                          ? TIMING_COLUMNS.map(({key}) => (
                              <td key={key}>{formatDuration(check[key])}</td>
                            ))
                          : null}
                      </tr>
                    ))}
                  </tbody>
                </ChecksTable>
              </TableScroll>
            ) : (
              <Message>
                No checks yet. Open a release on NZB.su and choose “Check” to add the first result.
              </Message>
            )}

            {recentChecks?.paginationToken ? (
              <LoadMore onClickAsync={handleLoadMoreClick} loading={loadingMore}>
                Load older checks
              </LoadMore>
            ) : (
              <></>
            )}
          </Panel>

          <Panel>
            <SectionHeader>
              <h2>Find a release</h2>
            </SectionHeader>
            <LookupRow>
              <Input<string>
                aria-label="NZB release ID"
                placeholder="Paste an NZB release ID"
                value={searchGuid}
                syncState={setSearchGuid}
              />
              <Button onClick={handleInspectClick} disabled={searchGuid.trim().length === 0}>
                View release
              </Button>
            </LookupRow>
          </Panel>
        </>
      )}
    </Wrapper>
  );
};

NzbHealthDebugPage.displayName = 'NzbHealthDebugPage';

function formatTimestamp(timestamp: number): string {
  return new Date(timestamp).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function formatDuration(durationMs: number | undefined): string {
  if (durationMs === undefined) {
    return '—';
  }
  return durationMs < 1000
    ? `${durationMs} ms`
    : `${(durationMs / 1000).toFixed(durationMs < 10_000 ? 1 : 0)} s`;
}

function getChecksPerMinute(checks: RecentNzbCheck[] | undefined): number | undefined {
  const sample = checks?.slice(0, CHECK_RATE_SAMPLE_SIZE);
  if (!sample || sample.length < 2) {
    return undefined;
  }

  const timestamps = sample.map(check => check.ts);
  const durationMs = Math.max(...timestamps) - Math.min(...timestamps);
  return durationMs > 0 ? (sample.length * 60_000) / durationMs : undefined;
}

function formatCheckRate(rate: number | undefined): string {
  if (rate === undefined || !Number.isFinite(rate)) {
    return '—';
  }
  const fractionDigits = rate < 1 ? 2 : rate < 10 ? 1 : 0;
  return Number(rate.toFixed(fractionDigits)).toString();
}

interface CheckedMovieProps {
  movie?: NzbCheckMovie;
}

const CheckedMovie: FC<CheckedMovieProps> = ({movie}) =>
  movie?.movieTitle && movie.imdbId ? (
    <MovieLink to={`/browse/${movie.imdbId}`} title={movie.movieTitle}>
      {movie.movieTitle}
    </MovieLink>
  ) : (
    <UnavailableMovie>{movie?.movieTitle ?? '—'}</UnavailableMovie>
  );

CheckedMovie.displayName = 'CheckedMovie';

interface TrackMetadataStatusProps {
  metadata?: NzbMediaMetadata;
}

const TrackMetadataStatus: FC<TrackMetadataStatusProps> = ({metadata}) => {
  if (!metadata) {
    return <TrackMetadataBadge $state="unknown">Not recorded</TrackMetadataBadge>;
  }

  if (metadata.status === 'complete') {
    const audioCount = metadata.audioTracks.length;
    const subtitleCount = metadata.subtitleTracks.length;
    return (
      <TrackMetadataBadge $state="complete">
        Complete ({audioCount} audio, {subtitleCount} subtitles)
      </TrackMetadataBadge>
    );
  }

  if (metadata.status === 'suspicious') {
    const labels: Record<string, string> = {
      'zero-filled-media-header': 'zero-filled media header',
      'encrypted-archive': 'password required',
      'missing-volume': 'missing archive volumes',
      'missing-article': 'missing Usenet articles',
      'missing-prefix': 'missing file prefix',
      'parity-only-nzb': 'PAR2 recovery files only',
    };
    return (
      <TrackMetadataBadge $state="suspicious" title={metadata.reason}>
        {metadata.errorCode
          ? `Suspicious (${labels[metadata.errorCode] ?? 'track metadata unknown'})`
          : 'Suspicious (no audio, no subtitles)'}
      </TrackMetadataBadge>
    );
  }

  if (metadata.status === 'error') {
    const errorDetails =
      [metadata.errorCode, metadata.reason].filter(Boolean).join(': ') || 'Unknown error';
    return (
      <TrackMetadataBadge $state="error" title={errorDetails}>
        {`Error: ${errorDetails}`}
      </TrackMetadataBadge>
    );
  }

  return (
    <TrackMetadataBadge
      $state="skipped"
      title={[metadata.errorCode, metadata.reason].filter(Boolean).join(': ')}
    >
      Skipped
    </TrackMetadataBadge>
  );
};

TrackMetadataStatus.displayName = 'TrackMetadataStatus';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  max-width: 1400px;
  width: 100%;
  align-self: center;
`;

const PageHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  gap: 16px;
  h1 {
    margin: 0;
    white-space: nowrap;
  }
`;

const Panel = styled.section`
  background: #1f242f;
  border: 1px solid #ffffff1a;
  border-radius: 8px;
  padding: 16px;
  h2 {
    margin: 0 0 6px;
    font-size: 18px;
  }
  p {
    margin: 0;
    color: #ffffffbb;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
  @media (max-width: 720px) {
    flex-direction: column;
  }
`;

const LookupRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  > input {
    flex: 1;
    min-width: 0;
  }
  @media (max-width: 560px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CheckRate = styled.span`
  color: #ffffffbb;
  font-size: 13px;
  white-space: nowrap;
`;

const ShowDetailedTimes = styled.label`
  display: flex;
  align-items: center;
  gap: 7px;
  color: #ffffffbb;
  font-size: 13px;
  white-space: nowrap;
  input {
    margin: 0;
  }
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const ChecksTable = styled.table`
  border-collapse: collapse;
  min-width: 100%;
  table-layout: auto;
  width: max-content;
  text-align: left;
  th {
    color: #ffffff88;
    font-size: 13px;
    font-weight: 500;
  }
  th,
  td {
    border-bottom: 1px solid #ffffff12;
    padding: 11px 10px;
    vertical-align: middle;
    white-space: nowrap;
  }
  td {
    padding-top: 0;
    padding-bottom: 0;
  }
  th:first-child,
  td:first-child {
    padding-left: 0;
  }
`;

const ReleaseTitle = styled.div`
  max-width: 420px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MovieLink = styled(UnthemedNavLink)`
  display: inline-block;
  max-width: 300px;
  color: #a9d2ff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DetailsLink = styled(UnthemedNavLink)`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 4px;
  color: #a9d2ff;
  background: #ffffff10;
  white-space: nowrap;
  &:hover {
    background: #ffffff18;
  }
`;

const SizeHeader = styled.th`
  text-align: right;
`;

const SizeCell = styled.td`
  text-align: right;
`;

const UnavailableMovie = styled.span`
  color: #ffffff77;
`;

const CategoryValue = styled.div`
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TotalDuration = styled.div`
  white-space: nowrap;
`;

const Guid = styled(UnthemedNavLink)`
  display: block;
  color: #a9d2ff;
  font-family: monospace;
  font-size: 12px;
  margin-top: 4px;
  overflow-wrap: anywhere;
  -webkit-user-select: text !important;
  user-select: text !important;
  &:hover {
    text-decoration: underline;
  }
`;

const ResultBadge = styled.span<{$state: 'empty' | 'missing' | 'found' | 'failed'}>`
  color: ${p =>
    p.$state === 'failed'
      ? '#ff8585'
      : p.$state === 'missing'
        ? '#ffcb8a'
        : p.$state === 'found'
          ? '#9ee6ae'
          : '#ffffff88'};
  white-space: nowrap;
`;

const TrackMetadataBadge = styled.span<{
  $state: 'complete' | 'error' | 'skipped' | 'unknown' | 'suspicious';
}>`
  display: inline-block;
  max-width: 240px;
  overflow: hidden;
  color: ${p =>
    p.$state === 'complete'
      ? '#9ee6ae'
      : p.$state === 'error'
        ? '#ff8585'
        : p.$state === 'skipped' || p.$state === 'suspicious'
          ? '#ffcb8a'
          : '#ffffff88'};
  text-overflow: ellipsis;
  vertical-align: bottom;
  white-space: nowrap;
`;

const ReleaseDate = styled.p`
  margin-top: 6px !important;
`;

const PreviousChecksHeading = styled.h3`
  font-size: 15px;
  margin: 0 0 4px;
`;

const FileDetailsHeading = styled.h3`
  font-size: 15px;
  margin: 0 0 8px;
`;

const FileMetadata = styled.dl`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 18px;
`;

const MetadataField = styled.div`
  min-width: 140px;
  max-width: 260px;
  padding: 8px 10px;
  border-radius: 6px;
  background: #ffffff08;
  overflow-wrap: anywhere;
  dt {
    margin-bottom: 3px;
    color: #ffffff88;
    font-size: 11px;
  }
  dd {
    margin: 0;
    color: #ffffffdd;
    font-size: 13px;
  }
`;

const LoadMore = styled(Button)`
  margin-top: 12px;
`;

const Message = styled.div`
  color: #ffffff88;
  padding: 8px 0;
`;

const ErrorMessage = styled(Message)`
  color: #ff8585;
`;
